import { defineStore } from 'pinia';
import { axiosSupportInstance, axiosV2APIInstance, makeRequest, type ApiEnvelope } from './middleware';

import type {
    ServiceDesk,
    RequestTypeGroup,
    RequestType,
    RequestFieldResponse,
    RequestDetails,
    RequestFilterData,
    RequestTypeStatus,
    Visitor,
    VisitorByOrgId,
    VisitorType,
    User,
    LocationFootprint,
    SupportRequestResponse,
} from './store-interfaces';
import { useOrganizationStore } from './organization';
import { computed } from 'vue';
import { getEnv } from '@/config/projectEnvVariables';
import { normalizeJiraWikiText } from '@/lib/jira-wiki';

function normalizeJiraWikiFieldValues(value: unknown): unknown {
    if (typeof value === 'string') {
        return normalizeJiraWikiText(value);
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeJiraWikiFieldValues(item));
    }

    if (value && Object.prototype.toString.call(value) === '[object Object]') {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [key, normalizeJiraWikiFieldValues(nestedValue)]),
        );
    }

    return value;
}

export const useSupportStore = defineStore('support', () => {
    const organizationStore = useOrganizationStore();
    const usersOrgId = computed(() => organizationStore.usersOrganization?.organization_id);

    async function getServiceDesks() {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<ServiceDesk[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/servicedesks`,
                axiosSupportInstance,
            );

            return response.data.data;
        } else return;
    }

    async function getRequestTypeGroups(serviceDeskId: number) {
        const response = await makeRequest<ApiEnvelope<RequestTypeGroup[]>>(
            'GET',
            `servicedesk-management/servicedesks/${serviceDeskId}/requesttypegroups`,
            axiosSupportInstance,
        );
        return response.data.data;
    }

    async function getRequestTypes(requestTypeGroupId: number) {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<RequestType[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/requesttypegroups/${requestTypeGroupId}/requesttypes`,
                axiosSupportInstance,
            );
            return response.data.data;
        } else throw new Error('Organization ID is not defined');
    }

    async function getRequestTypesByServiceDesk(serviceDeskId: number) {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<RequestType[]>>(
                'GET',
                `servicedesk-management/servicedesks/${serviceDeskId}/requesttypes`,
                axiosSupportInstance,
            );
            return response.data.data;
        }
    }

    async function getServiceDeskRequestTypes(serviceDeskId: number) {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<RequestType[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/servicedesks/${serviceDeskId}/requesttypes`,
                axiosSupportInstance,
            );
            return response.data.data;
        } else throw new Error('Organization ID is not defined');
    }

    async function getServiceDeskStatuses(serviceDeskId: number) {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<RequestTypeStatus[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/servicedesks/${serviceDeskId}/statuses`,
                axiosSupportInstance,
            );
            return response.data.data;
        } else throw new Error('Organization ID is not defined');
    }

    async function getRequestTypeFields(serviceDeskId: string, requestTypeId: string) {
        if (usersOrgId.value != undefined) {
            const response = await makeRequest<ApiEnvelope<RequestFieldResponse>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/servicedesks/${serviceDeskId}/requesttype/${requestTypeId}/fields`,
                axiosSupportInstance,
            );
            return response.data.data;
        }
    }

    async function getWorkItemAttachment(requestId: string, attachmentId: string) {
        if (usersOrgId.value !== undefined) {
            const baseUrl = getEnv().VITE_INTEGRATION_API_HOST;
            const response = await makeRequest<Blob>(
                'GET',
                baseUrl + `/servicedesk/workitems/${requestId}/attachments/${attachmentId}`,
                axiosV2APIInstance,
                null,
                { responseType: 'blob' }, // Ensure we get a Blob for the attachment
            );
            return response.data;
        }
    }

    async function getWorkItemAttachmentThumbnail(requestId: string, attachmentId: string) {
        if (usersOrgId.value !== undefined) {
            const baseUrl = getEnv().VITE_INTEGRATION_API_HOST;
            const response = await makeRequest<Blob>(
                'GET',
                baseUrl + `/servicedesk/workitems/${requestId}/attachments/${attachmentId}/thumbnail`,
                axiosV2APIInstance,
                null,
                { responseType: 'blob' }, // Ensure we get a Blob for the thumbnail,
            );
            return response.data;
        }
    }

    async function createRequest(
        serviceDeskId: string,
        requestTypeId: string,
        fields: Record<string, unknown>,
        files?: File[],
        siteId?: number,
        visitorList?: Record<string, unknown>,
        userList?: Record<string, unknown>,
        escortList?: Record<string, unknown>,
        locationFootprint?: LocationFootprint,
        participantList?: Record<string, unknown>,
    ) {
        const formData = new FormData();
        const normalizedFields = normalizeJiraWikiFieldValues(fields) as Record<string, unknown>;

        // Add simple fields to FormData
        formData.append('serviceDeskId', serviceDeskId);
        formData.append('requestTypeId', requestTypeId);
        if (siteId !== undefined) {
            formData.append('siteId', siteId.toString());
        }

        if (visitorList && Object.keys(visitorList).length > 0) {
            formData.append('visitorList', JSON.stringify(visitorList));
        }

        if (userList && Object.keys(userList).length > 0) {
            formData.append('userList', JSON.stringify(userList));
        }

        if (escortList && Object.keys(escortList).length > 0) {
            formData.append('escortList', JSON.stringify(escortList));
        }

        if (locationFootprint) {
            formData.append('locationFootprint', JSON.stringify(locationFootprint));
        }
        formData.append('requestFieldValues', JSON.stringify(normalizedFields));

        // Add files to FormData (if any)
        if (files && files.length > 0) {
            files.forEach((file) => {
                formData.append(`attachments`, file);
            });
        }

        if (participantList && Object.keys(participantList).length > 0) {
            formData.append('participantList', JSON.stringify(participantList));
        }

        // Make the request using FormData
        const response = await makeRequest<ApiEnvelope<{ issueId: string; issueKey: string }>>(
            'POST',
            `organization-management/organizations/${usersOrgId.value}/issues`,
            axiosSupportInstance,
            formData,
            {
                throwOnError: true,
                skipToast: true,
            },
        );

        return response.data;
    }

    async function postRequestComment(requestId: number, comment: string, files: File[]) {
        const formData = new FormData();
        formData.append('comment', normalizeJiraWikiText(comment));
        formData.append('serviceDeskId', '74');
        if (files && files.length > 0) {
            files.forEach((file) => {
                formData.append(`attachments`, file);
            });
        }

        const response = await makeRequest<ApiEnvelope<RequestDetails['comments']['values'][number] | undefined>>(
            'POST',
            `organization-management/organizations/${usersOrgId.value}/issues/${requestId}/comment`,
            axiosSupportInstance,
            formData,
        );
        return response.data?.data;
    }

    async function getRequests(filter: RequestFilterData, nextPageToken: string | null, maxResults: number) {
        if (usersOrgId.value !== undefined) {
            const queryParams = new URLSearchParams();
            if (filter.userIsParticipant) queryParams.append('showParticipation', filter.userIsParticipant.toString());
            if (filter.createdByUser) queryParams.append('showCreated', filter.createdByUser.toString());
            if (filter.requestTypes) queryParams.append('selectRequestTypes', filter.requestTypes);
            if (filter.statuses) queryParams.append('selectStatuses', filter.statuses);
            if (filter.serviceDeskId) queryParams.append('selectServiceDesks', filter.serviceDeskId.toString());
            if (filter.showOrganizationRequests) queryParams.append('showOrganization', '1');
            if (filter.referenceSearch) queryParams.append('referenceSearch', filter.referenceSearch);
            if (filter.summarySearch) queryParams.append('summarySearch', filter.summarySearch);
            if (nextPageToken) queryParams.append('nextPageToken', nextPageToken);
            queryParams.append('maxResults', maxResults.toString());

            const response = await makeRequest<ApiEnvelope<SupportRequestResponse>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/issues?${queryParams.toString()}`,
                axiosSupportInstance,
            );

            if (response.data !== undefined) {
                return response.data.data;
            } else return;
        }
    }

    async function getRequestDetails(requestId: number) {
        const response = await makeRequest<ApiEnvelope<RequestDetails>>(
            'GET',
            `organization-management/organizations/${usersOrgId.value}/issues/${requestId}`,
            axiosSupportInstance,
        );
        return response.data.data;
    }

    async function getVisitors() {
        if (usersOrgId.value !== undefined) {
            const response = await makeRequest<ApiEnvelope<VisitorByOrgId[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/visitors`,
                axiosSupportInstance,
            );
            return response.data.data;
        }
    }

    async function getVisitorTypes() {
        if (usersOrgId.value !== undefined) {
            const response = await makeRequest<ApiEnvelope<VisitorType[]>>(
                'GET',
                `organization-management/organizations/${usersOrgId.value}/visitors/types`,
                axiosSupportInstance,
            );
            return response.data.data;
        }
    }

    async function createVisitors(body: Array<{ name: string; organization_visitor_type_id: number }>) {
        if (usersOrgId.value !== undefined) {
            const response = await makeRequest(
                'POST',
                `organization-management/organizations/${usersOrgId.value}/visitors`,
                axiosSupportInstance,
                body,
            );
            return response.data;
        }
    }

    async function getVisitorsByRequestId(requestId: number) {
        const response = await makeRequest<ApiEnvelope<Visitor[]>>(
            'GET',
            `organization-management/organizations/${usersOrgId.value}/issues/${requestId}/visitors`,
            axiosSupportInstance,
        );
        return response.data.data;
    }

    async function getVisitingUsersByRequestId(requestId: number) {
        const response = await makeRequest<ApiEnvelope<User[]>>(
            'GET',
            `organization-management/organizations/${usersOrgId.value}/issues/${requestId}/users`,
            axiosSupportInstance,
        );
        return response.data.data;
    }

    return {
        getServiceDesks,
        getRequestTypeGroups,
        getRequestTypes,
        getRequestTypesByServiceDesk,
        getServiceDeskRequestTypes,
        getServiceDeskStatuses,
        getRequestTypeFields,
        getRequests,
        getRequestDetails,
        createRequest,
        postRequestComment,
        getVisitors,
        getVisitorTypes,
        createVisitors,
        getVisitorsByRequestId,
        getVisitingUsersByRequestId,
        getWorkItemAttachment,
        getWorkItemAttachmentThumbnail,
    };
});
